import { useState, useEffect } from "react";
import api from "../api";

export interface Bet {
  id: number;
  bet_id: string;
  description: string;
  amount: number; // nanotons
  amount_ton: number;
  status: "funded" | "live" | "resolved" | "expired" | "cancelled";
  challenger_name: string;
  challenger_telegram_id: number;
  creator_telegram_id: number;
  opponent_telegram_id: number;
  opponent_name: string;
  winner_id: number | null;
  bet_type?: string;
  category?: string;
  created_at?: string; // ISO datetime
  expires_at: string; // ISO datetime
  strava_bet_type: string | null;
  strava_target_value: number | null;
  bet_mode?: string;
  pile_on_type?: string;
  target_user_id?: number;
  target_telegram_id?: number;
  total_pot?: number;
  for_total?: number;
  against_total?: number;
  for_count?: number;
  against_count?: number;
  join_window_closes_at?: string;
  winner_side?: string;
}

export interface Stats {
  wins: number;
  losses: number;
  total: number;
  streak: number;
}

export interface StravaProgress {
  current_value: number;
  target_value: number;
  percentage: number;
}

/** Convert nanotons to "0.5 TON" display string */
export function formatTon(nanotons: number): string {
  const tons = nanotons / 1_000_000_000;
  return `${tons % 1 === 0 ? tons.toFixed(0) : tons.toFixed(1)} TON`;
}

/**
 * Relative time like "5d 3h", "23h 42m", "8m 17s".
 *
 * VEN-38: we always include the next smaller unit once the window drops under
 * a day, so creators and opponents can tell the difference between "2 hours
 * left" and "2 minutes left" at a glance. Under 1 hour the string updates
 * every second (BetDetailPage ticks the timer at 1s when short enough).
 */
export function formatTimeLeft(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "Expired";

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const DEV_BETS: Bet[] = [
  {
    id: 1,
    bet_id: "dev-bet-1",
    description: "Run 5 km this week",
    amount: 500_000_000,
    amount_ton: 0.5,
    status: "live",
    challenger_name: "you",
    challenger_telegram_id: 0,
    creator_telegram_id: 0,
    opponent_telegram_id: 999,
    opponent_name: "anna",
    winner_id: null,
    expires_at: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    strava_bet_type: "distance",
    strava_target_value: 5.0,
  },
  {
    id: 2,
    bet_id: "dev-bet-2",
    description: "Cycle 25 km",
    amount: 1_000_000_000,
    amount_ton: 1.0,
    status: "funded",
    challenger_name: "you",
    challenger_telegram_id: 0,
    creator_telegram_id: 0,
    opponent_telegram_id: 888,
    opponent_name: "mike",
    winner_id: null,
    expires_at: new Date(Date.now() + 23 * 60 * 60 * 1000).toISOString(),
    strava_bet_type: "distance",
    strava_target_value: 25.0,
  },
  {
    id: 3,
    bet_id: "dev-bet-3",
    description: "Run 10 km",
    amount: 500_000_000,
    amount_ton: 0.5,
    status: "resolved",
    challenger_name: "you",
    challenger_telegram_id: 0,
    creator_telegram_id: 0,
    opponent_telegram_id: 999,
    opponent_name: "anna",
    winner_id: 0,
    expires_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    strava_bet_type: "distance",
    strava_target_value: 10.0,
  },
  {
    id: 4,
    bet_id: "dev-bet-4",
    description: "Swim 1 km",
    amount: 2_000_000_000,
    amount_ton: 2.0,
    status: "resolved",
    challenger_name: "you",
    challenger_telegram_id: 0,
    creator_telegram_id: 0,
    opponent_telegram_id: 777,
    opponent_name: "jake",
    winner_id: 777,
    expires_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    strava_bet_type: "distance",
    strava_target_value: 1.0,
  },
];

const DEV_STATS: Stats = { wins: 2, losses: 1, total: 3, streak: 1 };

const DEV_BETS_MAP: Record<string, Bet> = Object.fromEntries(
  DEV_BETS.flatMap((b) => [[b.bet_id, b]])
);

/** Fetch a single bet by ID */
export function useBet(betId: string) {
  const [bet, setBet] = useState<Bet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchBet() {
      try {
        const res = await api.get(`/bets/${betId}`);
        const d = res.data;
        if (d && typeof d === "object" && typeof d.status === "string") {
          const normalized = {
            ...d,
            bet_id: d.bet_id,
            amount_ton: typeof d.amount_ton === "number" ? d.amount_ton : d.amount / 1_000_000_000,
            challenger_name: d.challenger_name || "",
            challenger_telegram_id: d.challenger_telegram_id || d.creator_telegram_id || 0,
          } as Bet;
          setBet(normalized);
          setLoading(false);
        } else {
          throw new Error("Invalid bet data");
        }
      } catch {
        if (import.meta.env.DEV && DEV_BETS_MAP[betId]) {
          setBet(DEV_BETS_MAP[betId]);
          setLoading(false);
        } else {
          setError("Bet not found");
          setLoading(false);
        }
      }
    }
    fetchBet();
  }, [betId]);

  return { bet, loading, error };
}

/** Format countdown as "23h 41m" with minute precision */
export function formatCountdown(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "Expired";

  const totalMin = Math.floor(diff / 60_000);
  const hours = Math.floor(totalMin / 60);
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  const remainMin = totalMin % 60;

  if (days > 0) return `${days}d ${remainHours}h`;
  return `${hours}h ${remainMin}m`;
}

export function useBets() {
  const [bets, setBets] = useState<Bet[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    // Dev mode without backend — use mock data immediately
    if (import.meta.env.DEV) {
      try {
        const [betsRes, statsRes] = await Promise.all([
          api.get("/bets"),
          api.get("/users/me/stats"),
        ]);
        const betsPayload = Array.isArray(betsRes.data?.bets)
          ? betsRes.data.bets
          : Array.isArray(betsRes.data)
            ? betsRes.data
            : [];
        if (Array.isArray(betsPayload)) {
          setBets(betsPayload);
          setStats(statsRes.data);
          setLoading(false);
          return;
        }
      } catch {
        // Backend not available — fall through to mock data
      }
      setBets(DEV_BETS);
      setStats(DEV_STATS);
      setLoading(false);
      return;
    }

    try {
      const [betsRes, statsRes] = await Promise.allSettled([
        api.get("/bets"),
        api.get("/users/me/stats"),
      ]);
      if (betsRes.status === "fulfilled") {
        setBets(
          Array.isArray(betsRes.value.data?.bets)
            ? betsRes.value.data.bets
            : Array.isArray(betsRes.value.data)
              ? betsRes.value.data
              : []
        );
      } else {
        setError("Failed to load bets");
      }
      if (statsRes.status === "fulfilled") {
        setStats(statsRes.value.data);
      }
    } catch {
      setError("Failed to load bets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  return { bets, stats, loading, error, refetch: fetchData };
}

export function useStravaProgress(betId: string) {
  const [progress, setProgress] = useState<StravaProgress | null>(null);

  useEffect(() => {
    const provider =
      (import.meta.env.VITE_FITNESS_PROVIDER as string | undefined) || "healthbridge";
    api
      .get(`/fitness/${provider}/check-bet/${betId}`)
      .then((res) => {
        const d = res.data;
        // Generic fitness endpoint returns progress_pct (0–100); our consumers
        // still read .percentage, so normalise here. Falls back to the legacy
        // `percentage` field if the backend ever returns the Strava shape.
        if (d && (typeof d.progress_pct === "number" || typeof d.percentage === "number")) {
          setProgress({
            current_value: d.current_value,
            target_value: d.target_value,
            percentage:
              typeof d.progress_pct === "number" ? d.progress_pct : d.percentage,
          });
        } else if (import.meta.env.DEV) {
          setProgress({ current_value: 3.2, target_value: 5.0, percentage: 64 });
        }
      })
      .catch(() => {
        if (import.meta.env.DEV) {
          setProgress({ current_value: 3.2, target_value: 5.0, percentage: 64 });
        }
      });
  }, [betId]);

  return progress;
}
